import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, map, switchMap, throwError } from 'rxjs';
import { ForgetPassword } from '../../../core/interfaces/forgetPassword';
import { ResetPassword } from '../../../core/interfaces/resetPassword';
import { User } from '../../../core/interfaces/user';
import { environment } from 'src/environments/environment';
import { Permissions } from 'src/assets/static-data/permissions';
import { IUserTypes } from 'src/assets/static-data/enums/user-types.enum';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    private baseApi = `${environment.apiURL}/api`;
    isLocked = new Subject<boolean>();
    isLoggedIn = new BehaviorSubject<boolean>(false);
    isDriverRoleSubject :BehaviorSubject<boolean>;
    isDriverRole :Observable<boolean>;
    isSalesAgentRoleSubject = new BehaviorSubject<boolean>(false);
    isSalesAgentRole: Observable<boolean>;
    isCustomerRoleSubject = new BehaviorSubject<boolean>(false);
    isCustomerRole: Observable<boolean>;
    permissions= new Permissions();
    IUserType=IUserTypes;
    constructor(private http: HttpClient) {
        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();

        //for driver role
        this.isDriverRoleSubject=new BehaviorSubject<boolean>(JSON.parse(localStorage.getItem('isDriverRole')));
        this.isDriverRole=this.isDriverRoleSubject.asObservable();

        //for sales agent role
        this.isSalesAgentRoleSubject=new BehaviorSubject<boolean>(JSON.parse(localStorage.getItem('isSalesAgentRole')));
        this.isSalesAgentRole=this.isSalesAgentRoleSubject.asObservable();

        //for customer role
        this.isCustomerRoleSubject=new BehaviorSubject<boolean>(JSON.parse(localStorage.getItem('isCustomerRole')));
        this.isCustomerRole=this.isCustomerRoleSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    public get isDriverUserValue():boolean{
        return this.isDriverRoleSubject.value;
    }

    public get isSalesAgentValue():boolean{
        return this.isSalesAgentRoleSubject.value;
    }

    public get isCustomerValue():boolean{
        return this.isCustomerRoleSubject.value;
    }

    login(email: string, password: string, deviceToken: string = '') {
        return this.http
          .post<any>(`${this.baseApi}/account/login`, { email, password, deviceToken })
          .pipe(
            map((user) => {
              // debugger
              // Handle login logic and set user details
              if (user && user.token) {
                localStorage.setItem('currentUser', JSON.stringify(user));
                //the default is arabic
                user.language?localStorage.setItem('lang', user.language):localStorage.setItem('lang', 'ar');
                this.isLoggedIn.next(true);
                this.currentUserSubject.next(user);
      
                // Set role-based permissions
                this.setRoles(user);
      
                return user;
              }
              throw new Error('Invalid login response');
            }),
            switchMap((user) => {
              // Authorization logic for customer portal
              if (!this.isCustomerValue && environment.portalType === 'customer-portal') {
                this.logout();
                return throwError(() => new Error("You don't have permission to access this portal"));
              }
              else if(this.isCustomerValue && environment.portalType === 'main-portal'){
                this.logout();
                return throwError(() => new Error("You don't have permission to access this portal"));
              }
              return [user]; // Pass user data downstream
            }),
            catchError((error) => {
              // Optional: You can modify or log the error here if needed
              return throwError(() => error); // Propagate the error to the subscription
            })
          );
      }
      
      // Helper function to set roles
      private setRoles(user: any) {
        // Driver role
        // debugger
        if (user.userType==this.IUserType.Driver) {
          this.isDriverRoleSubject.next(true);
          localStorage.setItem('isDriverRole', JSON.stringify(true));
        } else {
          this.isDriverRoleSubject.next(false);
          localStorage.setItem('isDriverRole', JSON.stringify(false));
        }
      
        // Sales agent role
        if (user.userType==this.IUserType.SalesAgent) {
          this.isSalesAgentRoleSubject.next(true);
          localStorage.setItem('isSalesAgentRole', JSON.stringify(true));
        } else {
          this.isSalesAgentRoleSubject.next(false);
          localStorage.setItem('isSalesAgentRole', JSON.stringify(false));
        }
      
        // Customer role
        if (user.userType==this.IUserType.Customer) {
          this.isCustomerRoleSubject.next(true);
          localStorage.setItem('isCustomerRole', JSON.stringify(true));
        } else {
          this.isCustomerRoleSubject.next(false);
          localStorage.setItem('isCustomerRole', JSON.stringify(false));
        }
      }

    logout() {
        let userId = this.currentUserValue?.id;
        if(!userId){
            return;
        }
        let token = this.currentUserValue.deviceToken;
        // remove user from local storage to log user out
        localStorage.clear();
        this.currentUserSubject.next(null);
        return this.http.post<any>(`${this.baseApi}/account/logout`, {userId, token});
    }

    changePassword(changePasswordDto) {
        return this.http.patch<any>(`${this.baseApi}/account/changePassword`, changePasswordDto);
    }

    forgotPassword(email: string){
        const forgetPasswordDto = new ForgetPassword();
        forgetPasswordDto.email = email;
        forgetPasswordDto.clientURI = "http://localhost:4200/reset-password";
        return this.http.post<any>(`${this.baseApi}/account/forgotPassword`,forgetPasswordDto);
    }

    resetPassword(resetPasswordDto: ResetPassword)
    {
        return this.http.post<any>(`${this.baseApi}/account/resetPassword`,resetPasswordDto);
    }
}
